import React, { useState } from 'react';
import moment from 'moment';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterInput from '../../../../components/Filter/components/FilterInput';
import { translate } from '../../../../services/translate';
import CustomButton from '../../../../components/CustomButton';
import { faBarsSort } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import MyAppText from '../../../../components/MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';
import { ScrollView } from 'react-native-web-hover';
import { AppAgentParamList } from '../../../../typings/Params';
import FilterDatePicker from '../../../../components/Filter/components/FilterDateInput';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';

interface Props {
    filters: AppAgentParamList['Candidacies'];
    setFilters: React.Dispatch<React.SetStateAction<AppAgentParamList['Candidacies']>>;
    textFilter: string;
    setTextFilter: (value: string) => void;
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<AppAgentParamList, 'Candidacies'>>>();
    const { styles, theme } = useStyles(styleSheet);

    const [isOpen, setIsOpen] = useState(false);
    const [beginInDateFilter, setBeginInDateFilter] = useState(filters.beginIn ? new Date(filters.beginIn) : null);
    const [beginInHourFilter, setBeginInHourFilter] = useState(filters.beginIn ? moment(filters.beginIn).format('HH:mm') : '');
    const [endInDateFilter, setEndInDateFilter] = useState(filters.endIn ? new Date(filters.endIn) : null);
    const [endInHourFilter, setEndInHourFilter] = useState(filters.endIn ? moment(filters.endIn).format('HH:mm') : '');

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const handleCleanFilter = () => {
        setTextFilter('');
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
    };

    const handleFilter = () => {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        navigation.setParams({
            textFilter: textFilter !== '' ? textFilter : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
        });

        setFilters({
            ...filters,
            textFilter: textFilter !== '' ? textFilter : undefined,
            beginIn: beginIn && !isNaN(beginIn) ? beginIn : undefined,
            endIn: endIn && !isNaN(endIn) ? endIn : undefined,
        });
    };

    return (
        <>
            <CustomButton
                onPress={() => setIsOpen(!isOpen)}
                text={translate('filters')}
                icon={faBarsSort as IconDefinition}
                type='secondary'
            />
            <Modal
                transparent
                visible={isOpen}
                animationType='slide'
                onRequestClose={() => { }}
            >
                <View style={{ flex: 1, height: '100%', width: '100%', backgroundColor: theme.colors.container.background, paddingHorizontal: 16, paddingTop: 16 }}>
                    <View style={styles.header}>
                        <View>
                            <MyAppText style={{ fontSize: 18 }}>{translate('allFilters')}</MyAppText>
                        </View>

                        <FontAwesomeIcon
                            icon={faX as IconDefinition}
                            color={theme.colors.iconColor}
                            fontSize={12}
                            cursor='pointer'
                            onClick={() => setIsOpen(!isOpen)}
                        />
                    </View>
                    <View style={[styles.filterContainer]}>
                        <View style={styles.inputsHeight}>
                            <ScrollView contentContainerStyle={styles.scrollContent}>
                                <View style={styles.children}>
                                    <FilterInput
                                        label={translate('typeToSearch')}
                                        value={textFilter}
                                        placeholder={translate('typeToSearch')}
                                        onChange={setTextFilter}
                                    />

                                    <View style={{ zIndex: estimatedStartZIndex }}>
                                        <FilterDatePicker
                                            date={beginInDateFilter}
                                            onChangeDate={setBeginInDateFilter}
                                            time={beginInHourFilter}
                                            onChangeTime={setBeginInHourFilter}
                                            label={translate('beginIn')}
                                            onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                                            onCalendarClose={() => setEstimatedStartZIndex(2)}
                                            labelStyle={styles.selectLabelStyle}
                                        />
                                    </View>

                                    <View style={{ zIndex: estimatedFinishZIndex }}>
                                        <FilterDatePicker
                                            date={endInDateFilter}
                                            onChangeDate={setEndInDateFilter}
                                            time={endInHourFilter}
                                            onChangeTime={setEndInHourFilter}
                                            label={translate('endIn')}
                                            onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                                            onCalendarClose={() => setEstimatedFinishZIndex(1)}
                                            labelStyle={styles.selectLabelStyle}
                                        />
                                    </View>
                                </View>
                            </ScrollView>
                            <View style={styles.buttonsContainer}>
                                <CustomButton
                                    text={translate('filterResults')}
                                    onPress={() => {
                                        handleFilter();
                                        setIsOpen(!isOpen);
                                    }}
                                />
                                <CustomButton text={translate('cleanFilters')} onPress={handleCleanFilter} type='secondary' />
                            </View>
                        </View>
                    </View>
                </View>
            </Modal>
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 40,
    },
    selectLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    filterContainer: {
        paddingBottom: 16,
        flex: 1
    },
    inputsHeight: {
        flex: 1,
        gap: 16
    },
    buttonsContainer: {
        flexDirection: 'column',
        gap: 10,
    },
    children: {
        zIndex: 2,
        gap: 10
    },
    scrollContent: {
        flex: 1,
        gap: 20,
        justifyContent: 'space-between',
    }
}));

export default Filter;
